import {useEffect} from "react";
import {InfoCircleFilled} from "@ant-design/icons";
import {useFormContext} from "react-hook-form";
import {pricingOptions} from "../../offering-form/session-bundle/session/pricing/currencies/default-currency-form-field/currency-select/data";
import {useGetSupportedCurrencies} from "../../../react-query-hooks/general";

const ALL_CURRENCY_CODES = pricingOptions.map((option) => option.value.toUpperCase());

interface AutomaticCurrencyConversionNoticeProps {
  /** Default price currency conversions are sourced from. */
  sourceCurrency?: string;
}

/**
 * Automatic currency conversion is always enforced, so there is nothing to toggle here.
 * This renders the explanatory notice and keeps "selected_currencies" filled with every
 * currency convertible from the default price currency.
 */
export function AutomaticCurrencyConversionNotice({
  sourceCurrency,
}: AutomaticCurrencyConversionNoticeProps) {
  const {setValue, getValues} = useFormContext();
  const {data: supportedCurrencies} = useGetSupportedCurrencies(sourceCurrency);

  useEffect(() => {
    // Fall back to the full currency list until the API responds.
    const nextCurrencies = supportedCurrencies?.length
      ? supportedCurrencies.map((code) => code.toUpperCase())
      : ALL_CURRENCY_CODES;
    const currentCurrencies: string[] = getValues("selected_currencies") ?? [];

    if (currentCurrencies.join("|") !== nextCurrencies.join("|")) {
      setValue("selected_currencies", nextCurrencies, {
        shouldValidate: true,
      });
    }
  }, [supportedCurrencies, getValues, setValue]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        color: "#595959",
        fontSize: "1.4rem",
      }}
    >
      <InfoCircleFilled style={{marginTop: "4px", color: "#1C1726"}} />
      <span>
        Your default price will be automatically converted to other supported currencies.
        Manually added currencies override automatic conversion.
      </span>
    </div>
  );
}
